import { RefObject, useEffect } from 'react';
import { DataGridUtils } from '@fashionnovateam/nova-ui-toolkit';

type GridApiRef = ReturnType<typeof DataGridUtils.useGridApiRef>;

const MAX_RETRIES = 10;
const INITIAL_RETRY_DELAY = 100;

export function useScrollSync(
  apiRef: GridApiRef,
  headerScrollRef: RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    let scrollContainer: Element | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let retryCount = 0;

    const handleScroll = () => {
      const headerEl = headerScrollRef.current;
      if (headerEl && scrollContainer) {
        headerEl.scrollLeft = scrollContainer.scrollLeft;
      }
    };

    const setupScrollSync = (): boolean => {
      const gridRoot = apiRef?.current?.rootElementRef?.current;
      scrollContainer =
        gridRoot?.querySelector('.MuiDataGrid-virtualScroller') ?? null;

      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll);
        return true;
      }
      return false;
    };

    const attemptSetup = () => {
      if (setupScrollSync()) {
        return;
      }

      retryCount += 1;
      if (retryCount < MAX_RETRIES) {
        // Exponential backoff: 100ms, 200ms, 400ms, etc.
        const delay = INITIAL_RETRY_DELAY * 2 ** (retryCount - 1);
        timeoutId = setTimeout(attemptSetup, delay);
      }
    };

    attemptSetup();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      scrollContainer?.removeEventListener('scroll', handleScroll);
    };
  }, [apiRef, headerScrollRef]);
}
